import React, { useRef, useId } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CloudUploadOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

const isDocx = (file) => (file?.name || '').toLowerCase().endsWith('.docx');

const PseudoDocxDropzone = ({ disabled, hasFile, onFilesAdded }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const inputRef = useRef(null);
  const inputId = useId();

  const processFiles = (fileList) => {
    if (!fileList || !fileList.length) return;

    const files = Array.from(fileList);
    const first = files[0];

    if (!isDocx(first)) {
      enqueueSnackbar(
        t('PseudoDocxDropzone.onlyDocx', 'Podporované sú iba .docx súbory.'),
        { variant: 'warning' }
      );
      return;
    }

    onFilesAdded([first]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || hasFile) return;
    processFiles(e.dataTransfer?.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInputChange = (e) => {
    if (disabled || hasFile) return;
    processFiles(e.target.files);
    e.target.value = '';
  };

  const handleClick = () => {
    if (disabled || hasFile) return;
    inputRef.current?.click();
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onClick={handleClick}
      sx={(theme) => ({
        border: '2px dashed',
        borderColor: 'primary.main',
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        borderRadius: 3,
        p: 4,
        textAlign: 'center',
        mb: 2,
        cursor: disabled || hasFile ? 'not-allowed' : 'pointer',
        opacity: disabled || hasFile ? 0.6 : 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          bgcolor: disabled || hasFile ? 'background.paper' : alpha(theme.palette.primary.main, 0.1),
          borderColor: 'primary.main',
        },
      })}
    >
      <Stack alignItems="center" spacing={1.5}>
        <CloudUploadOutlined sx={{ fontSize: 48, color: 'primary.main', opacity: 0.8 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t('PseudoDocxDropzone.title', 'Presuňte sem .docx alebo kliknite pre výber')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('PseudoDocxDropzone.subtitle', 'Spracujeme jeden súbor naraz (pseudonymizácia).')}
          </Typography>
        </Box>
      </Stack>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: 'none' }}
        onChange={handleInputChange}
        disabled={disabled || hasFile}
      />
    </Box>
  );
};

export default PseudoDocxDropzone;